import axios from './axios';

const CATALOG_KEY = 'taa-shop-catalog-v4';
const PRODUCT_STORE_PREFIX = 'taa-shop-p-v1:';
const CATALOG_TTL_MS = 5 * 60 * 1000;
const PRODUCT_TTL_MS = 5 * 60 * 1000;
const QUOTE_TTL_MS = 60 * 1000;

export const EMPTY_SHOP_CATALOG = {
  categories: [],
  products: [],
  deals: [],
  promotions: [],
  cooperatives: [],
  social_proof: [],
};

const productCache = new Map();
const quoteCache = new Map();
let catalogRequest = null;

function readCatalogRecord() {
  try {
    const raw = localStorage.getItem(CATALOG_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data || typeof parsed.data !== 'object') return null;
    return parsed;
  } catch {
    return null;
  }
}

export function readCachedCatalog() {
  const data = readCatalogRecord()?.data || null;
  if (data) seedProductsFromCatalog(data);
  return data;
}

function seedProductsFromCatalog(data) {
  (data?.products || []).forEach((product) => {
    if (!product?.slug) return;
    const entry = productCache.get(product.slug);
    if (entry?.data?.reviews?.length || entry?.data?.product?.description) return;
    productCache.set(product.slug, {
      at: Date.now(),
      req: entry?.req,
      partial: true,
      data: {
        product,
        related: entry?.data?.related || [],
        reviews: entry?.data?.reviews || [],
      },
    });
  });
}

export function writeCachedCatalog(data) {
  try {
    localStorage.setItem(CATALOG_KEY, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* quota / private mode */
  }
}

function refreshCatalogInBackground() {
  prefetchShopCatalog({ force: true }).catch(() => {});
}

export function prefetchShopCatalog(options = {}) {
  const record = readCatalogRecord();
  const age = record?.at ? Date.now() - record.at : Infinity;
  if (!options.force && record?.data && age < CATALOG_TTL_MS) {
    seedProductsFromCatalog(record.data);
    if (age > CATALOG_TTL_MS / 2) refreshCatalogInBackground();
    return Promise.resolve(record.data);
  }
  if (catalogRequest) return catalogRequest;
  catalogRequest = axios
    .get('/api/shop/')
    .then((res) => {
      const data = res.data || EMPTY_SHOP_CATALOG;
      writeCachedCatalog(data);
      seedProductsFromCatalog(data);
      return data;
    })
    .catch((error) => {
      if (record?.data) return record.data;
      throw error;
    })
    .finally(() => {
      catalogRequest = null;
    });
  return catalogRequest;
}

export function productSlugFromPath(pathname = typeof window !== 'undefined' ? window.location.pathname : '') {
  const match = String(pathname || '').match(/\/marketplace\/p\/([^/?#]+)/i);
  if (!match) return '';
  try {
    return decodeURIComponent(match[1]);
  } catch {
    return match[1];
  }
}

function readPersistedProduct(slug) {
  if (!slug) return null;
  try {
    const raw = sessionStorage.getItem(`${PRODUCT_STORE_PREFIX}${slug}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed?.data?.product?.slug) return null;
    if (Date.now() - parsed.at > PRODUCT_TTL_MS) return null;
    return parsed.data;
  } catch {
    return null;
  }
}

function persistProduct(slug, data) {
  if (!slug || !data?.product?.slug) return;
  try {
    sessionStorage.setItem(`${PRODUCT_STORE_PREFIX}${slug}`, JSON.stringify({ at: Date.now(), data }));
  } catch {
    /* quota / private mode */
  }
}

function preloadProductImage(data) {
  if (typeof document === 'undefined') return;
  const product = data?.product;
  const url = product?.gallery?.find((item) => item?.url && item.type !== 'video')?.url || product?.image_url;
  if (!url) return;
  if (document.querySelector(`link[data-taa-product-img="${url}"]`)) return;
  const link = document.createElement('link');
  link.rel = 'preload';
  link.as = 'image';
  link.href = url;
  link.setAttribute('data-taa-product-img', url);
  document.head.appendChild(link);
}

function storeProduct(slug, data, extra = {}) {
  productCache.set(slug, {
    at: Date.now(),
    req: extra.req || Promise.resolve(data),
    data,
    partial: Boolean(extra.partial),
  });
  if (data && !extra.partial) persistProduct(slug, data);
  preloadProductImage(data);
}

export function readCachedProduct(slug) {
  if (!slug) return null;
  const entry = productCache.get(slug);
  if (entry?.data && Date.now() - entry.at <= PRODUCT_TTL_MS) return entry.data;
  const persisted = readPersistedProduct(slug);
  if (persisted) {
    storeProduct(slug, persisted);
    return persisted;
  }
  return null;
}

function landingBootRequest(slug) {
  if (typeof window === 'undefined') return null;
  const boot = window.__TAA_SHOP_BOOT;
  if (!boot || boot.slug !== slug || !boot.req || boot.consumed) return null;
  boot.consumed = true;
  return Promise.resolve(boot.req).then((payload) => {
    if (!payload?.product) throw new Error('empty product');
    return payload;
  });
}

export function prefetchShopProduct(slug, options = {}) {
  if (!slug) return Promise.resolve(null);
  const bootReq = landingBootRequest(slug);
  const entry = productCache.get(slug);
  if (!bootReq && entry?.req && Date.now() - entry.at < PRODUCT_TTL_MS) return entry.req;
  if (!bootReq && entry?.data && !entry.partial && Date.now() - entry.at < PRODUCT_TTL_MS) {
    return Promise.resolve(entry.data);
  }
  const persisted = entry?.data || readPersistedProduct(slug);
  const network = bootReq
    ? bootReq.catch(() => axios.get(`/api/shop/products/${slug}/`).then((res) => res.data))
    : axios.get(`/api/shop/products/${slug}/`).then((res) => res.data);
  const req = network
    .then((data) => {
      storeProduct(slug, data, { req: Promise.resolve(data), partial: false });
      if (!options.shallow) {
        (data?.related || []).slice(0, 4).forEach((item) => {
          if (item?.slug && item.slug !== slug) prefetchShopProduct(item.slug, { shallow: true }).catch(() => {});
        });
      }
      return data;
    })
    .catch((error) => {
      if (persisted?.product) {
        storeProduct(slug, persisted, { req: Promise.resolve(persisted), partial: Boolean(entry?.partial) });
        return persisted;
      }
      productCache.delete(slug);
      throw error;
    });
  productCache.set(slug, {
    at: Date.now(),
    req,
    data: persisted || null,
    partial: Boolean(entry?.partial),
  });
  return req;
}

export function bootShopFromLocation(pathname) {
  const slug = productSlugFromPath(pathname);
  if (!slug) return;
  const persisted = readPersistedProduct(slug);
  if (persisted && !productCache.get(slug)?.data) storeProduct(slug, persisted);
  prefetchShopProduct(slug, { shallow: true }).catch(() => {});
}

if (typeof window !== 'undefined') {
  bootShopFromLocation();
}

function quoteKey(items, city) {
  const lines = (items || [])
    .map((item) => `${item.slug}:${item.qty}`)
    .filter(Boolean)
    .sort()
    .join(',');
  return `${lines}|${String(city || '').trim().toLowerCase()}`;
}

export function readCachedQuote(items = [], city = '') {
  if (!items.length) return null;
  const hit = quoteCache.get(quoteKey(items, city));
  if (!hit || Date.now() - hit.at > QUOTE_TTL_MS) return null;
  return hit.data || null;
}

export function prefetchCheckoutQuote(items = [], city = '') {
  if (!items.length) return Promise.resolve(null);
  const key = quoteKey(items, city);
  const hit = quoteCache.get(key);
  if (hit?.req && Date.now() - hit.at < QUOTE_TTL_MS) return hit.req;
  const req = axios
    .post('/api/shop/checkout-summary/', {
      items: (items || []).map((item) => ({ slug: item.slug, qty: item.qty })),
      city: String(city || '').trim(),
    })
    .then((res) => {
      const data = res.data || null;
      quoteCache.set(key, { at: Date.now(), req: Promise.resolve(data), data });
      return data;
    })
    .catch((error) => {
      quoteCache.delete(key);
      throw error;
    });
  quoteCache.set(key, { at: Date.now(), req, data: hit?.data || null });
  return req;
}
